import { FC, memo } from 'react';
import { Box, Flex, HStack, Stack, Text } from '@chakra-ui/react';

import {
  ConfirmDialog,
  useConfirmDialog,
} from '~/core/shared/components/ConfirmDialog';
import { SummaryContentComponentElement } from '~/core/shared/types';
import { ContentElement } from '../ContentElement.component';
import { EditDetailsMenu } from '../EditDetailsMenu';
import { useSummaryBlockElement } from './hooks/useSummaryBlockElement';
import { useSummaryBlockElementStyle } from './hooks/useSummaryBlockElementStyle';
import { SummaryBlockElementProps } from './types/SummaryBlockElementProps';

const SummaryBlockElementComponent: FC<SummaryBlockElementProps> = ({
  content,
  contentElements,
  documentType,
}) => {
  const styles = useSummaryBlockElementStyle(content.identifier);
  const { handleSideEffects, controls, entries, totalEntries } =
    useSummaryBlockElement(content, contentElements);
  const confirmDialog = useConfirmDialog();

  if (!totalEntries) return null;

  return (
    <Flex css={styles.box} direction="column">
      <Box css={styles.headerBox}>
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
        >
          <Text css={styles.headerText}>{content.title}</Text>
        </Stack>
      </Box>
      <Stack css={styles.contentBox}>
        {entries.map((entry, index) => {
          const [blockId, contentElementsForDocuments] = entry;
          return (
            <HStack key={blockId} alignItems="flex-start">
              <Flex
                borderBottom={
                  totalEntries > 1 && index < totalEntries - 1
                    ? '1px solid'
                    : 'none'
                }
                borderBottomColor={
                  totalEntries > 1 && index < totalEntries - 1
                    ? 'gray.200'
                    : 'transparent'
                }
                direction="row"
                flexWrap="wrap"
                mb={4}
                pb={4}
                w="full"
              >
                {contentElementsForDocuments.map(
                  (element: SummaryContentComponentElement) => (
                    <ContentElement
                      key={element.id}
                      css={styles.contentElement}
                      element={element}
                    />
                  ),
                )}
              </Flex>
              <Flex>
                <EditDetailsMenu
                  blockId={blockId}
                  controls={controls}
                  documentType={documentType}
                  handleSideEffects={handleSideEffects}
                  onOpenConfirm={confirmDialog.onOpen}
                  title={content.title}
                />
              </Flex>
            </HStack>
          );
        })}
      </Stack>

      <ConfirmDialog {...confirmDialog} />
    </Flex>
  );
};

export const SummaryBlockElement = memo(SummaryBlockElementComponent);
